if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        navigation: this.querySelector('[data-media-gallery-navigation-controls]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.imagePreparations = new WeakMap();
      this.navigationPending = 0;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.requestActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      this.elements.navigation?.querySelectorAll('[data-media-gallery-navigation]').forEach((button) => {
        button.addEventListener('click', this.navigateMedia.bind(this));
      });
      this.updateNavigationButtons();
      this.prepareAdjacentMedia();
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    requestActiveMedia(mediaId, prepend) {
      const targetMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      const targetImage = targetMedia?.querySelector('.product__modal-opener--image img');
      const requestId = ++this.navigationPending;

      if (!targetImage || this.isImageReady(targetImage)) {
        this.elements.navigation?.removeAttribute('aria-busy');
        this.setActiveMedia(mediaId, prepend);
        return;
      }

      this.elements.navigation?.setAttribute('aria-busy', 'true');
      this.prepareImage(targetImage).finally(() => {
        if (requestId !== this.navigationPending) return;

        this.elements.navigation?.removeAttribute('aria-busy');
        this.setActiveMedia(mediaId, prepend);
      });
    }

    isImageReady(image) {
      return image.complete && image.naturalWidth > 0;
    }

    prepareImage(image) {
      const existingPreparation = this.imagePreparations.get(image);
      if (existingPreparation) return existingPreparation;

      image.loading = 'eager';

      const preparation = new Promise((resolve) => {
        if (image.complete) {
          resolve(image.naturalWidth > 0);
          return;
        }

        let timeout;
        const finish = () => {
          window.clearTimeout(timeout);
          resolve(image.naturalWidth > 0);
        };

        image.addEventListener('load', finish, { once: true });
        image.addEventListener('error', finish, { once: true });
        timeout = window.setTimeout(finish, 12000);
      }).then((loaded) => {
        if (!loaded) {
          this.imagePreparations.delete(image);
          return false;
        }
        if (typeof image.decode !== 'function') return true;
        return image.decode().catch(() => {}).then(() => true);
      });

      this.imagePreparations.set(image, preparation);
      return preparation;
    }

    prepareAdjacentMedia(activeThumbnail = null) {
      const thumbnails = this.getNavigableThumbnails();
      if (thumbnails.length <= 1) return;

      const currentThumbnail = activeThumbnail
        || this.elements.thumbnails.querySelector('button[aria-current="true"]')?.closest('[data-target]');
      const currentIndex = thumbnails.indexOf(currentThumbnail);
      if (currentIndex === -1) return;

      const adjacentThumbnails = new Set([
        thumbnails[(currentIndex - 1 + thumbnails.length) % thumbnails.length],
        thumbnails[(currentIndex + 1) % thumbnails.length]
      ]);

      adjacentThumbnails.forEach((thumbnail) => {
        const media = this.elements.viewer.querySelector(`[data-media-id="${ thumbnail.dataset.target }"]`);
        const image = media?.querySelector('.product__modal-opener--image img');
        if (image) this.prepareImage(image);
      });
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      this.updateNavigationButtons();
      this.prepareAdjacentMedia(thumbnail);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    navigateMedia(event) {
      const thumbnails = this.getNavigableThumbnails();
      const activeThumbnail = this.elements.thumbnails.querySelector('button[aria-current="true"]')?.closest('[data-target]');
      const activeIndex = thumbnails.indexOf(activeThumbnail);
      const direction = event.currentTarget.dataset.mediaGalleryNavigation === 'next' ? 1 : -1;
      const referenceIndex = activeIndex === -1 ? (direction === 1 ? -1 : 0) : activeIndex;

      if (thumbnails.length <= 1) return;

      const targetIndex = (referenceIndex + direction + thumbnails.length) % thumbnails.length;
      const targetThumbnail = thumbnails[targetIndex];

      if (targetThumbnail) targetThumbnail.querySelector('button').click();
    }

    getNavigableThumbnails() {
      return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
        (thumbnail) => !thumbnail.matches('.thumbnail-list_item--variant:not(:first-child)')
      );
    }

    updateNavigationButtons() {
      if (!this.elements.navigation) return;

      const thumbnails = this.getNavigableThumbnails();
      const previousButton = this.elements.navigation.querySelector('[data-media-gallery-navigation="previous"]');
      const nextButton = this.elements.navigation.querySelector('[data-media-gallery-navigation="next"]');
      const navigationUnavailable = thumbnails.length <= 1;

      this.elements.navigation.hidden = navigationUnavailable;
      previousButton.disabled = navigationUnavailable;
      nextButton.disabled = navigationUnavailable;
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
